import { useCallback, useEffect, useState } from 'react';

import { canInstall, getUnavailableReason, isRunningStandalone, promptInstall, subscribe } from 'utils/pwaInstall';

/**
 * Whether this app can be installed, and how to do it.
 *
 * The `beforeinstallprompt` capture lives in utils/pwaInstall, which is imported
 * at application start. It has to: the browser fires that event once and never
 * replays it, so a listener registered when this hook first mounted routinely
 * missed it - and the Install App button then stayed disabled forever on an app
 * that was perfectly installable. This hook only subscribes to that store.
 *
 * The previous version also trusted a `pwa_installed` flag in localStorage. That
 * is removed: uninstalling an app does not clear localStorage, so anyone who
 * installed once and later removed it was told "App Installed" permanently, with
 * no way back. Installed-ness is now derived from display-mode, which the
 * browser actually knows.
 *
 * @returns {{
 *   isInstallable: boolean,
 *   isInstalled: boolean,
 *   unavailableReason: 'installed'|'unsupported'|'pending'|null,
 *   installApp: () => Promise<boolean>
 * }}
 */
export default function usePWAInstall() {
  const read = () => ({
    isInstallable: canInstall(),
    isInstalled: isRunningStandalone(),
    unavailableReason: getUnavailableReason()
  });

  const [state, setState] = useState(read);

  useEffect(() => {
    const sync = () => setState(read());
    const unsubscribe = subscribe(sync);

    // display-mode changes when the app is launched as a PWA, or when the user
    // returns to the browser tab afterwards.
    const media = window.matchMedia?.('(display-mode: standalone)');
    media?.addEventListener?.('change', sync);

    // Re-read on focus: the app may have been installed from the browser's own
    // menu, in another tab, while this one was in the background.
    window.addEventListener('focus', sync);

    sync();
    return () => {
      unsubscribe();
      media?.removeEventListener?.('change', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const installApp = useCallback(async () => {
    const outcome = await promptInstall();
    return outcome === 'accepted';
  }, []);

  return { ...state, installApp };
}
